import { api, ApiError } from "../api.js";
import { esc, cohortIcons, avatarHtml, displayName, timeAgo, formatDateTime, toast } from "../utils.js";
import { navigate } from "../app.js";
import { getCurrentUser, isSiteAdmin } from "../auth.js";
import { confirmAction } from "../confirmDialog.js";
import { manageMenuHtml } from "../manageMenu.js";

/**
 * One photo, large, with who uploaded it and what it is of.
 *
 * Reached from any gallery. The context chips are whatever the photo is
 * tagged with — a photo uploaded to a dive event carries its site and
 * cohort too, so all three can appear; a photo attached to a plain post
 * may carry none.
 */
export async function render(container, params) {
  const photoId = params.id;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  let photo;
  try {
    photo = await api.get(`/photos/${photoId}`);
  } catch (err) {
    container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Photo not found.</div></div>`;
    return;
  }

  const uploader = photo.owner ? displayName(photo.owner) : "Someone";
  // Whoever uploaded it, or a moderator — the same rule the API
  // enforces and the same one the gallery tiles use.
  const me = getCurrentUser();
  // Same rule for both editing and removing: whoever uploaded it,
  // or a moderator.
  const canManage = me != null && (photo.owner?.id === me.id || isSiteAdmin());

  const chips = [];
  if (photo.dive_site) {
    chips.push(
      `<button type="button" class="badge text-bg-light border me-2 mb-2 context-chip" data-go="/dive-site/${photo.dive_site.id}">
         <i class="bi bi-geo-alt me-1"></i>${esc(photo.dive_site.name)}
       </button>`
    );
  }
  if (photo.dive_plan) {
    chips.push(
      `<button type="button" class="badge text-bg-light border me-2 mb-2 context-chip" data-go="/dive-plan/${photo.dive_plan.id}">
         <i class="bi bi-calendar-event me-1"></i>${esc(photo.dive_plan.title)}
       </button>`
    );
  }
  if (photo.community) {
    chips.push(
      `<button type="button" class="badge text-bg-light border me-2 mb-2 context-chip" data-go="/cohort/${photo.community.id}">
         ${cohortIcons(photo.community)}${esc(photo.community.name)}
       </button>`
    );
  }

  container.innerHTML = `
    <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
    <div class="card mb-4">
      <div class="card-body">
        <!-- Caption left, manage menu right — the same header row the
             dive site and dive event pages use. The row renders even
             with no caption, so the menu sits in the same place either
             way. -->
        <div class="d-flex justify-content-between align-items-start mb-2 gap-2">
          ${photo.caption
            ? `<h1 class="display-heading h5 mb-0" id="photo-caption">${esc(photo.caption)}</h1>`
            : `<span></span>`}
          ${manageMenuHtml(
            [
              { id: "photo-edit", icon: "bi-pencil", label: "Edit" },
              { id: "photo-remove", icon: "bi-trash", label: "Delete", danger: true },
            ].filter(() => canManage),
            { label: "Manage this photo" }
          )}
        </div>
        ${canManage
          ? `<form id="photo-edit-form" class="d-none mb-3">
               <div class="mb-2">
                 <label class="form-label small mb-1" for="photo-caption-input">Caption</label>
                 <input type="text" class="form-control form-control-sm" id="photo-caption-input"
                        maxlength="200" value="${esc(photo.caption ?? "")}"
                        placeholder="A short title for this photo" />
               </div>
               <div class="mb-2">
                 <label class="form-label small mb-1" for="photo-description-input">Description</label>
                 <!-- A textarea, not an input: line breaks are kept and
                      the page renders them. -->
                 <textarea class="form-control form-control-sm" id="photo-description-input"
                           rows="4" maxlength="2000"
                           placeholder="Depth, conditions, gear — anything worth remembering.">${esc(photo.description ?? "")}</textarea>
               </div>
               <div id="photo-edit-error" class="text-danger small mb-2 d-none"></div>
               <div class="d-flex gap-2">
                 <button type="submit" class="btn btn-sm btn-primary" id="photo-edit-save">Save</button>
                 <button type="button" class="btn btn-sm btn-outline-secondary" id="photo-edit-cancel">Cancel</button>
               </div>
             </form>`
          : ""}
        <!-- Contained rather than cropped: this is the page you came to in
             order to see the whole picture. Capped so a tall portrait shot
             does not push everything else off screen. -->
        <!-- The caption doubles as alt text: a real description of the
             picture beats "Photo uploaded by Ana" for anyone using a
             screen reader. -->
        <img src="${esc(photo.image_url)}"
             alt="${esc(photo.caption || `Photo uploaded by ${uploader}`)}"
             class="card-img-top bg-body-tertiary"
             style="object-fit:contain;max-height:70vh;" />
        <div class="d-flex align-items-center mb-3">
          ${avatarHtml(uploader, { className: "me-2 uploader-link", attrs: `data-user-id="${photo.owner?.id ?? ""}"` })}
          <div>
            <div class="fw-semibold uploader-link" data-user-id="${photo.owner?.id ?? ""}">${esc(uploader)}</div>
            <div class="text-slate small" title="${esc(formatDateTime(photo.created_at))}">
              Added ${esc(timeAgo(photo.created_at))}
            </div>
          </div>
        </div>
        ${photo.description
          ? `<p class="mb-3 dc-user-text">${esc(photo.description)}</p>`
          : ""}
        ${chips.length
          ? `<div class="mb-1">${chips.join("")}</div>`
          : `<p class="text-slate small mb-1">No dive site, event or cohort is tagged on this photo.</p>`}
        ${photo.post_id
          ? `<button type="button" class="btn btn-sm btn-outline-primary mt-2" id="open-post-btn">
               <i class="bi bi-chat me-1"></i>Go to the post
             </button>`
          : ""}
      </div>
    </div>`;

  container.querySelector("#back-btn").addEventListener("click", () => history.back());

  container.querySelectorAll(".context-chip").forEach((chip) => {
    chip.addEventListener("click", () => navigate(chip.dataset.go));
  });

  container.querySelectorAll(".uploader-link").forEach((el) => {
    el.addEventListener("click", () => {
      if (el.dataset.userId) navigate(`/user/${el.dataset.userId}`);
    });
  });

  const postBtn = container.querySelector("#open-post-btn");
  if (postBtn) postBtn.addEventListener("click", () => navigate(`/post/${photo.post_id}`));

  const editBtn = container.querySelector("#photo-edit");
  const editForm = container.querySelector("#photo-edit-form");
  if (editBtn && editForm) {
    editBtn.addEventListener("click", () => {
      editForm.classList.toggle("d-none");
      if (!editForm.classList.contains("d-none")) {
        container.querySelector("#photo-caption-input").focus();
      }
    });

    container.querySelector("#photo-edit-cancel").addEventListener("click", () => {
      // Re-rendering from the server's copy is simpler than restoring
      // fields by hand, and cannot drift from what was actually saved.
      render(container, params);
    });

    editForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const errorEl = container.querySelector("#photo-edit-error");
      const saveBtn = container.querySelector("#photo-edit-save");
      errorEl.classList.add("d-none");
      saveBtn.disabled = true;
      try {
        await api.patch(`/photos/${photo.id}`, {
          caption: container.querySelector("#photo-caption-input").value.trim(),
          // Sent as-is apart from the ends: newlines are part of what
          // was written, and the page renders them with pre-wrap.
          description: container.querySelector("#photo-description-input").value.trim(),
        });
        await render(container, params);
      } catch (err) {
        // Inline, beside the fields being corrected.
        errorEl.textContent =
          err instanceof ApiError ? err.message : "Could not save that.";
        errorEl.classList.remove("d-none");
        saveBtn.disabled = false;
      }
    });
  }

  const removeBtn = container.querySelector("#photo-remove");
  if (removeBtn) {
    removeBtn.addEventListener("click", async () => {
      const confirmed = await confirmAction({
        title: "Remove this photo?",
        body: "It will disappear from every gallery it appears in. The post it was attached to, if any, stays.",
        confirmLabel: "Remove",
      });
      if (!confirmed) return;
      removeBtn.disabled = true;
      try {
        await api.delete(`/photos/${photo.id}`);
        // Back to wherever you came from — usually the gallery, which
        // re-renders on navigation and so drops the photo on its own.
        // Staying put would leave the page showing something gone.
        history.back();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : "Could not remove that photo.", "danger");
        removeBtn.disabled = false;
      }
    });
  }
}
